import { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import type { QuizQuestion } from "../types";

interface QuizProps {
  questions: QuizQuestion[];
}

export const Quiz = ({ questions }: QuizProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number | number[]>>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    if (showResults) return;
    const question = questions.find((q) => q.id === questionId);
    const isMultiple = question?.multipleChoice || (question?.correctAnswers !== undefined && Array.isArray(question.correctAnswers));
    
    if (isMultiple) {
      setSelectedAnswers((prev) => {
        const current = (prev[questionId] as number[]) || [];
        const newAnswers = current.includes(answerIndex)
          ? current.filter((idx) => idx !== answerIndex)
          : [...current, answerIndex];
        return { ...prev, [questionId]: newAnswers };
      });
    } else {
      setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerIndex }));
    }
  };

  const handleSubmit = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };

  const correctCount = questions.filter((q) => {
    const selected = selectedAnswers[q.id];
    const isMultiple = q.multipleChoice || (q.correctAnswers !== undefined && Array.isArray(q.correctAnswers));
    if (isMultiple) {
      const correct = q.correctAnswers || [];
      const selectedArray = Array.isArray(selected) ? selected : [];
      return (
        selectedArray.length === correct.length &&
        selectedArray.every((idx) => correct.includes(idx)) &&
        correct.every((idx) => selectedArray.includes(idx))
      );
    } else {
      return selected === q.correctAnswer;
    }
  }).length;
  const totalQuestions = questions.length;
  const score = Math.round((correctCount / totalQuestions) * 100);

  const allAnswered = questions.every((q) => {
    const selected = selectedAnswers[q.id];
    const isMultiple = q.multipleChoice || (q.correctAnswers !== undefined && Array.isArray(q.correctAnswers));
    if (isMultiple) {
      return Array.isArray(selected) && selected.length > 0;
    } else {
      return selected !== undefined;
    }
  });

  return (
    <div className="my-4 sm:my-6 border border-neutral-border rounded-xl bg-white shadow-sm p-4 sm:p-6">

      <div className="space-y-4 sm:space-y-6">
        {questions.map((question) => {
          const selectedAnswer = selectedAnswers[question.id];
          // Определяем множественный выбор: если есть correctAnswers или явно указан multipleChoice
          const isMultiple = question.multipleChoice || (question.correctAnswers !== undefined && Array.isArray(question.correctAnswers));
          const isCorrect = isMultiple
            ? (() => {
                const correct = question.correctAnswers || [];
                const selectedArray = Array.isArray(selectedAnswer) ? selectedAnswer : [];
                return (
                  selectedArray.length === correct.length &&
                  selectedArray.every((idx) => correct.includes(idx)) &&
                  correct.every((idx) => selectedArray.includes(idx))
                );
              })()
            : selectedAnswer === question.correctAnswer;

          return (
            <div key={question.id} className="border border-neutral-border rounded-lg p-3 sm:p-4">
              <div className="mb-2 sm:mb-3">
                <div className="flex items-start gap-2">
                  {question.questionNumber && (
                    <span className="text-xs sm:text-sm font-semibold text-neutral-text/60">
                      {question.questionNumber}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm sm:text-base font-medium text-neutral-text leading-snug">
                      {question.question}
                    </p>
                    {isMultiple && (
                      <p className="text-[11px] sm:text-xs text-neutral-text/50 mt-0.5 sm:mt-1">
                        (можно выбрать несколько вариантов)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                {question.options.map((option, optionIndex) => {
                  const isSelected = isMultiple
                    ? Array.isArray(selectedAnswer) && selectedAnswer.includes(optionIndex)
                    : selectedAnswer === optionIndex;
                  const isCorrectOption = isMultiple
                    ? (question.correctAnswers || []).includes(optionIndex)
                    : optionIndex === question.correctAnswer;

                  let buttonClass =
                    "w-full text-left px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border transition-all text-sm sm:text-base";
                  
                  if (showResults) {
                    if (isCorrectOption) {
                      buttonClass += " bg-green-50 border-green-300 text-green-900";
                    } else if (isSelected && !isCorrectOption) {
                      buttonClass += " bg-red-50 border-red-300 text-red-900";
                    } else {
                      buttonClass += " bg-neutral-50 border-neutral-border text-neutral-text/60";
                    }
                  } else {
                    if (isSelected) {
                      buttonClass += " bg-primary/10 border-primary text-primary";
                    } else {
                      buttonClass += " bg-white border-neutral-border hover:border-primary/60 hover:bg-primary/5";
                    }
                  }

                  return (
                    <button
                      key={optionIndex}
                      onClick={() => handleAnswerSelect(question.id, optionIndex)}
                      disabled={showResults}
                      className={buttonClass}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        {showResults && isCorrectOption && (
                          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                        )}
                        {showResults && isSelected && !isCorrect && (
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                        )}
                        {!showResults && (
                          isMultiple ? (
                            <div
                              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-neutral-border"
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          ) : (
                            <div
                              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border-2 flex-shrink-0 ${
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-neutral-border"
                              }`}
                            >
                              {isSelected && (
                                <div className="w-full h-full rounded-full bg-white scale-50" />
                              )}
                            </div>
                          )
                        )}
                        <span className="flex-1 min-w-0">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {showResults && question.explanation && (
                <div className="mt-2 sm:mt-3 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm text-blue-900">
                  {question.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showResults ? (
        <div className="mt-4 sm:mt-6 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className="px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Проверить ответы
          </button>
        </div>
      ) : (
        <div className="mt-4 sm:mt-6 flex flex-col gap-2 sm:gap-3">
          {score === 100 && (
            <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                <span className="text-xl sm:text-2xl">🎉</span>
                <span className="font-semibold text-base sm:text-lg">Отлично!</span>
              </div>
              <p className="text-xs sm:text-sm">Вы отлично усвоили материал!</p>
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={handleReset}
              className="px-3 py-2 sm:px-4 sm:py-2 text-sm bg-white border border-neutral-border rounded-lg hover:bg-primary/10 transition"
            >
              Пройти заново
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

